import type { BillingCycle, SubscriptionInfo, TierCode } from '../api/billingApi';
import { TIER_ORDER } from '../constants/tierCopy';

// 로그인·구독 상태에 따른 분기 판정
export type CtaActionType = 'guest' | 'current' | 'trial' | 'upgrade' | 'downgrade' | 'cycle-change';

/** STARTER·PRO·ALL-IN 전부 동일한 규칙(사용자 결정, 2026-09-07): 체험·결제 이력이 전혀 없으면
 *  어떤 티어를 고르든 1개월 무료체험, 한 번이라도 체험했거나(trialEndDtm, 다른 티어였어도 무관)
 *  결제한 적 있으면(hasEverPaid, 다른 티어였어도 무관) 체험 없이 바로 결제. */
function isTrialEligible(tierCode: TierCode, subscription: SubscriptionInfo): boolean {
  return tierCode !== 'FREE' && !subscription.trialEndDtm && !subscription.hasEverPaid;
}

export function resolveCtaAction(
  tierCode: TierCode,
  isLoggedIn: boolean,
  subscription: SubscriptionInfo | undefined,
  billingCycle: BillingCycle,
): CtaActionType {
  if (!isLoggedIn) return 'guest';

  // 구독 자체가 없으면(SUBSCRIPTION_NOT_FOUND) 어떤 티어도 "이용중"이 아니다 — FREE도 명시적
  // 신청 전까지는 존재하지 않는 것으로 취급한다(사용자 결정, 2026-08-27). FREE를 기본 currentTier로
  // 가정하면 안 함 — 그러면 미신청 사용자에게 FREE 카드가 "이용중"으로 잘못 표시된다.
  if (!subscription) return tierCode !== 'FREE' ? 'trial' : 'upgrade';

  if (subscription.tierCode === tierCode) {
    // 결제 주기 변경(사용자 결정, 2026-09-07 "요금제 비교 화면에서는 변경할 수 있어") — 지금 이용
    // 중인 티어 카드를, 토글이 현재 구독 주기와 다른 값을 가리킬 때 누르면 여기로 온다. FREE는
    // 결제 주기 개념이 없어(billingCycle 자체가 null) 제외. ACTIVE 상태에서만 허용한다(코드 검수,
    // 2026-09-07) — TRIAL은 결제 주기 자체가 없어 여기서 허용하면 서버가 "새 주기 정가 전액"을
    // 즉시 청구해버려 무료체험이 조기 종료·과금되고, CANCELED·PAST_DUE·GRACE는 결제가 해지 예약·
    // 연체 상태를 의도치 않게 되돌리는 부작용이 있다.
    if (
      tierCode !== 'FREE' &&
      subscription.subStatus === 'ACTIVE' &&
      subscription.billingCycle != null &&
      subscription.billingCycle !== billingCycle
    ) {
      return 'cycle-change';
    }
    return 'current';
  }

  if (isTrialEligible(tierCode, subscription)) return 'trial';

  // 체험·결제 이력이 있어 여기로 떨어지면(FREE 상태에서 처음 결제하는 경우 포함) 랭크 비교로
  // 자연스럽게 'upgrade'/'downgrade'가 나온다 — 화면에는 hasEverPaid 여부로 "시작하기"/"변경하기"가
  // 표시된다(resolveCtaLabel 참고).
  const clickedRank = TIER_ORDER.indexOf(tierCode);
  const currentRank = TIER_ORDER.indexOf(subscription.tierCode);
  return clickedRank > currentRank ? 'upgrade' : 'downgrade';
}

/**
 * 버튼 표시 문구. STARTER·PRO·ALL-IN 전부 동일 규칙(사용자 결정, 2026-09-07):
 * - action='trial'이면 티어 무관하게 "N개월 무료체험 시작"(trialMonths — 추천 코드가 있으면
 *   보너스가 더해진 값을 호출부가 넘긴다, tierCopy.ts getTrialMonths 참고, 2026-09-08).
 * - action='upgrade'/'downgrade'면 **지금 체험 중이거나(`isTrialing`) 한 번이라도 결제한 적
 *   있으면**(`hasEverPaid`, 환불로 FREE로 돌아갔어도 영구히 true — trialEndDtm과 같은 성격)
 *   "변경하기", 둘 다 아니면 "시작하기"(2026-09-17 — 체험 중도 이미 요금제를 선택해 이용 중인
 *   상태라는 사용자 결정으로 isTrialing 분기 추가). 지금 당장 유료중인지가 아니라 "결제 이력이
 *   있는지"로 판단한다 — currentTierCode만으로는 환불 후 FREE로 돌아온 케이스를 "이력 있음"으로
 *   못 잡아서 hasEverPaid를 따로 쓴다.
 */
export function resolveCtaLabel(
  action: CtaActionType,
  defaultLabel: string,
  hasEverPaid: boolean,
  billingCycle: BillingCycle | undefined,
  trialMonths: number,
  isTrialing: boolean,
): string {
  if (action === 'current') return '이용중';
  if (action === 'cycle-change') return billingCycle === 'annual' ? '연간 결제로 전환' : '월간 결제로 전환';
  if (action === 'trial') return `${trialMonths}개월 무료체험 시작`;
  if (action !== 'upgrade' && action !== 'downgrade') return defaultLabel;
  // 지금 체험 중이면(subStatus='TRIAL') hasEverPaid(결제 이력)와 무관하게 "변경하기" — 사용자 결정
  // (2026-09-17, "체험 중일 때도 요금제를 이미 선택해 이용 중이니 다른 플랜을 고르는 건 '시작'이
  // 아니라 '변경'이다"). hasEverPaid 기준은 "결제 이력이 아예 없는" 진짜 첫 시작 케이스만 남긴다.
  if (isTrialing) return '변경하기';
  return hasEverPaid ? '변경하기' : defaultLabel;
}

/** 서버가 잔여일수를 직접 내려주기 전까지의 임시 계산(원칙적으로 서버 KST 자정 기준 판정이 정본) */
export function calcTrialDaysLeft(trialEndDtm: string | null): number | null {
  if (!trialEndDtm) return null;
  const diffMs = new Date(trialEndDtm).getTime() - Date.now();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}
